import React, { useContext, useState, useRef, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';

import { getMembership } from '@techstyle/react-accounts';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
} from '@techstyle/react-intl';

import { useStoreCredit as setStoreCredit } from '../../actions/cart';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { useLDNewShoppingBagUX } from '../../utils/LD/useNewShoppingBagUX';
import {
  getCreditsApplied,
  getCreditsRemaining,
  getNextMemberCredit,
  getStoreCredits,
} from '../../utils/selectors';
import { useToastMessage } from '../CartDrawer/useToastMessage';
import MemberCreditsCartToggle from '../MemberCreditsCartToggle/MemberCreditsCartToggle';
import { withShoppingBagRevampUX } from '../MemberCreditsCartToggle/WithShoppingBagRevampUX';
import { Component as MemberCreditsModal } from '../MemberCreditsModal';
import { ProgressBarNMP } from '../ProgressBarNMP/ProgressBarNMP';

import { CreditToggleButton } from './MemberCreditsCartToggleButton';
import {
  CreditsAmountInfo,
  CreditStatusContent,
  KeepShoppingSection,
  MemberCreditsLoading,
  PromoSection,
} from './shared';
import { useMemberCreditsToastMessage } from './useMemberCreditsToastMessage';
import { useUpdateCreditStatus } from './useUpdateCreditStatus';
import {
  CreditStatus,
  shouldRenderToggle,
  shouldRenderv2Components,
  useGetCartTotalWithoutConflictingPromos,
} from './utils';

export const NO_CREDITS_REMAINING_TIMEOUT_MS = 3000;

const defaultMessages = defineMessages({
  applied: {
    id: 'global_checkout.member_credit_applied',
    defaultMessage: 'Member Credit Applied',
  },
  applying: {
    id: 'global_checkout.applying_member_credit',
    defaultMessage: 'Applying Member Credit...',
  },
  available: {
    id: 'global_checkout.apply_member_credit',
    defaultMessage: 'Apply Member Credit',
  },
  no_credits_remaining: {
    id: 'global_checkout.no_member_credits_remaining',
    defaultMessage: 'Remove a member credit from another item',
  },
  removing: {
    id: 'global_checkout.removing_member_credit',
    defaultMessage: 'Removing Member Credit...',
  },
  unavailable: {
    id: 'global_checkout.member_credits_unavailable',
    defaultMessage: 'No Member Credits Available',
  },
});

const AlignmentWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledContainer = styled.div`
  padding: ${({ isShoppingBagRevampUX, theme }) =>
    isShoppingBagRevampUX ? '0 12px 12px' : `${theme.spacing.tiny}px`};
  background: ${({ isCreditApplied, isShoppingBagRevampUX, theme }) => {
    if (isShoppingBagRevampUX) {
      return theme.colors.marble;
    }
    return isCreditApplied ? theme.colors.marble : '#FAFAFD';
  }};
`;

const Container = withShoppingBagRevampUX(StyledContainer);

const StyledToggleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  margin: ${props => props.theme.spacing.tiny}px
    ${props => props.theme.spacing.tiny}px 0;
  border-radius: 4px 4px 0 0;

  background: ${({ isCreditApplied, isShoppingBagRevampUX, theme }) => {
    if (isShoppingBagRevampUX) {
      return theme.colors.marble;
    }
    return isCreditApplied ? theme.colors.marble : '#FAFAFD';
  }};
  ${({ isShoppingBagRevampUX }) =>
    isShoppingBagRevampUX &&
    `
    margin: 0;
  `}
`;

const ToggleContainer = withShoppingBagRevampUX(StyledToggleContainer);

const StyledStatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
`;
const StatusContainer = withShoppingBagRevampUX(StyledStatusContainer);

function MemberCreditsCartToggleNMP() {
  const { 'token-to-cash-v2': tokenToCashv2Enabled } = useLDFlags();

  const { formatMessage } = useIntl();

  const dispatch = useDispatch();

  const cart = useSelector(state => state.checkout);

  const cartHasPromoConflicts = cart.promoConflicts?.length;

  const availableMemberCredits = useSelector(getCreditsRemaining);

  const nextCreditPrice = useSelector(getNextMemberCredit);

  const { storeCreditsOnly } = useSelector(getStoreCredits);

  const hasStoreCredits = useSelector(
    state => getMembership(state).storeCreditBalance > 0
  );

  const memberCreditsApplied = useSelector(getCreditsApplied) || 0;

  const creditsUsed =
    memberCreditsApplied > 0 || (cartHasPromoConflicts && storeCreditsOnly > 0);

  const shouldDisplayNoCreditsRemainingTimeout = useRef();
  const creditsRemaining = availableMemberCredits - memberCreditsApplied;

  const [creditStatus, setCreditStatus] = useState(CreditStatus.available);
  const isCreditApplied = creditStatus === CreditStatus.applied;
  const [shouldDisplayNoCreditsRemaining, setShouldDisplayNoCreditsRemaining] =
    useState(false);

  const context = useContext(MemberCreditsCartToggle.Context);
  const shoppingBagFlagStatus = useLDNewShoppingBagUX();
  const { context: themeContext } = useTheme();

  const { showToastMessage, hideToastMessage } = useToastMessage();
  const showErrorMessage = useCallback(() => {
    const toastMessage = (
      <FormattedMessage
        id="global_checkout.something_went_wrong"
        defaultMessage="Something went wrong. Please try again."
      />
    );
    hideToastMessage();
    showToastMessage(toastMessage, 6000, true);
  }, [hideToastMessage, showToastMessage]);

  const cartTotalWithoutConflictingPromos =
    useGetCartTotalWithoutConflictingPromos();

  useMemberCreditsToastMessage({ creditsRemaining });
  useUpdateCreditStatus({
    creditsRemaining,
    creditsUsed,
    storeCreditsOnly,
    setCreditStatus,
    shouldDisplayNoCreditsRemaining,
  });

  const handleToggle = useCallback(async () => {
    const currentCreditStatus = creditStatus;

    if (
      creditStatus === CreditStatus.unavailable ||
      creditStatus === CreditStatus.no_credits_remaining
    ) {
      clearTimeout(shouldDisplayNoCreditsRemainingTimeout.current);
      shouldDisplayNoCreditsRemainingTimeout.current = setTimeout(
        () => setShouldDisplayNoCreditsRemaining(false),
        NO_CREDITS_REMAINING_TIMEOUT_MS
      );
      return setShouldDisplayNoCreditsRemaining(true);
    }

    // We can't cancel in-flight API requests so we ignore clicks while applying/removing
    if (
      creditStatus === CreditStatus.applying ||
      creditStatus === CreditStatus.removing
    ) {
      return;
    }

    setCreditStatus(
      creditStatus === CreditStatus.applied
        ? CreditStatus.removing
        : CreditStatus.applying
    );

    try {
      await dispatch(setStoreCredit(!isCreditApplied));
      if (shoppingBagFlagStatus?.isVariant && themeContext?.cartDrawer) {
        showToastMessage(
          isCreditApplied ? (
            <FormattedMessage
              id="global_checkout.remove_member_credits_toast"
              defaultMessage="Your Member Credits were successfully removed!"
            />
          ) : (
            <FormattedMessage
              id="global_checkout.apply_member_credits_toast"
              defaultMessage="Your Member Credits were successfully applied!"
            />
          )
        );
      }
    } catch (e) {
      setCreditStatus(currentCreditStatus);
      if (shoppingBagFlagStatus?.isVariant && themeContext?.cartDrawer) {
        showErrorMessage();
      }
    }
  }, [
    creditStatus,
    dispatch,
    isCreditApplied,
    shoppingBagFlagStatus,
    showErrorMessage,
    showToastMessage,
    themeContext.cartDrawer,
  ]);

  const statusText = formatMessage(defaultMessages[creditStatus]);

  const toggleIsOffButOverThreshold =
    shouldRenderToggle(cartTotalWithoutConflictingPromos, {
      memberCreditsApplied,
      hasStoreCredits,
      hasMemberCredits: availableMemberCredits > 0,
      hasPromoConflicts: cartHasPromoConflicts,
      membershipPrice: nextCreditPrice,
    }) &&
    creditsUsed === 0 &&
    cartTotalWithoutConflictingPromos >= nextCreditPrice;

  const shouldShowToggle = shouldRenderToggle(
    cartTotalWithoutConflictingPromos,
    {
      memberCreditsApplied,
      hasStoreCredits,
      hasMemberCredits: availableMemberCredits > 0,
      hasPromoConflicts: cartHasPromoConflicts,
      membershipPrice: nextCreditPrice,
    }
  );

  const shouldShowProgress =
    !toggleIsOffButOverThreshold &&
    shouldRenderv2Components(
      tokenToCashv2Enabled,
      creditsRemaining,
      creditsUsed,
      cartHasPromoConflicts
    );

  // If no Context provider, throw a helpful error
  if (!context) {
    throw new Error(
      '<MemberCreditsCartToggle /> must be wrapped in <MemberCreditsCartToggle.Provider />'
    );
  }

  if (!shouldShowToggle && !tokenToCashv2Enabled) {
    return null;
  }

  if (!hasStoreCredits && availableMemberCredits === 0) {
    return null;
  }
  if (
    !cartHasPromoConflicts &&
    hasStoreCredits &&
    availableMemberCredits === 0
  ) {
    return null;
  }
  if (
    !hasStoreCredits &&
    availableMemberCredits > 0 &&
    cartHasPromoConflicts &&
    cartTotalWithoutConflictingPromos < nextCreditPrice
  ) {
    return null;
  }

  return (
    <Container isCreditApplied={isCreditApplied}>
      <MemberCreditsLoading creditStatus={creditStatus} />
      <ToggleContainer isCreditApplied={isCreditApplied}>
        <StatusContainer>
          <AlignmentWrapper>
            <CreditStatusContent
              cartHasPromoConflicts={cartHasPromoConflicts}
              creditStatus={creditStatus}
              statusText={statusText}
              shouldShowProgress={shouldShowProgress}
            />
            <MemberCreditsModal />
          </AlignmentWrapper>
          <CreditsAmountInfo
            availableMemberCredits={availableMemberCredits}
            tokenToCashv2Enabled={tokenToCashv2Enabled}
            membershipPrice={nextCreditPrice}
            creditsRemaining={creditsRemaining}
            memberCreditsApplied={memberCreditsApplied}
          />
        </StatusContainer>
        {shouldShowToggle && (
          <CreditToggleButton
            isCreditApplied={isCreditApplied}
            creditStatus={creditStatus}
            handleToggle={handleToggle}
          />
        )}
      </ToggleContainer>
      {shouldShowProgress && <ProgressBarNMP />}
      <PromoSection
        cartHasPromoConflicts={cartHasPromoConflicts}
        tokenToCashv2Enabled={tokenToCashv2Enabled}
        isCreditApplied={isCreditApplied}
        membershipPrice={nextCreditPrice}
      />
      {shouldShowProgress && <KeepShoppingSection />}
    </Container>
  );
}

export default MemberCreditsCartToggleNMP;
